/**
 * Carregamento e cache de fotos de alunos.
 *
 * O host das fotos (R2/Cloudflare) responde com `access-control-allow-origin: *`,
 * então dá para buscar a imagem por fetch e converter para base64 sem depender de
 * proxies externos nem de imagens já renderizadas na tela. O base64 gerado aqui é
 * usado tanto na exibição quanto na geração de PDF (contrato/relatórios), o que
 * garante que a foto impressa é sempre a do aluno selecionado.
 *
 * O cache é global (window.photoCache) e por URL, e requisições simultâneas para a
 * mesma URL compartilham a mesma promise.
 */

import { useEffect, useState } from 'react';

const cache = (window.photoCache = window.photoCache || new Map());
const inflight = new Map();

const DEFAULT_SIZE = 400;
const DEFAULT_QUALITY = 0.9;
const FETCH_TIMEOUT_MS = 15000;

/**
 * Retorna a foto já convertida, se estiver em cache.
 */
export const getCachedPhoto = (photoUrl) => (photoUrl ? cache.get(photoUrl) || null : null);

/**
 * URLs alternativas para exibição direta em <img>.
 * Só o Google Drive (fotos legadas) precisa de variações; os demais discos usam a URL direta.
 */
export const getDisplayUrls = (photoUrl) => {
  if (!photoUrl) return [];
  if (!photoUrl.includes('drive.google.com')) return [photoUrl];

  const fileIdMatch =
    photoUrl.match(/[?&]id=([a-zA-Z0-9-_]+)/) || photoUrl.match(/\/d\/([a-zA-Z0-9-_]+)/);
  if (!fileIdMatch) return [photoUrl];

  const fileId = fileIdMatch[1];
  return [
    `https://lh3.googleusercontent.com/d/${fileId}`,
    `https://drive.google.com/thumbnail?id=${fileId}&sz=w400-h400`,
    `https://drive.google.com/uc?export=view&id=${fileId}`,
    photoUrl,
  ];
};

/**
 * Converte o blob em um JPEG quadrado mantendo a proporção original (recorte central,
 * com viés para o topo para não cortar o rosto). A imagem vem de um object URL, então
 * o canvas não é contaminado e o toDataURL funciona.
 */
const normalizeBlobToJpeg = (blob, size, quality) =>
  new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(blob);
    const img = new Image();

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;

        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, size, size);

        const side = Math.min(img.naturalWidth, img.naturalHeight);
        const sx = (img.naturalWidth - side) / 2;
        // 25% em vez de centralizado: em fotos verticais o rosto costuma ficar na parte de cima
        const sy = (img.naturalHeight - side) * 0.25;

        ctx.drawImage(img, sx, sy, side, side, 0, 0, size, size);
        resolve(canvas.toDataURL('image/jpeg', quality));
      } catch (error) {
        reject(error);
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Arquivo não é uma imagem válida'));
    };

    img.src = objectUrl;
  });

/**
 * Busca a foto e devolve um data URL (JPEG base64). Devolve null se não for possível.
 */
export const fetchPhotoAsBase64 = (photoUrl, { size = DEFAULT_SIZE, quality = DEFAULT_QUALITY } = {}) => {
  if (!photoUrl) return Promise.resolve(null);
  if (cache.has(photoUrl)) return Promise.resolve(cache.get(photoUrl));
  if (inflight.has(photoUrl)) return inflight.get(photoUrl);

  const request = (async () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

    try {
      const response = await fetch(photoUrl, {
        mode: 'cors',
        credentials: 'omit',
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const base64 = await normalizeBlobToJpeg(await response.blob(), size, quality);
      cache.set(photoUrl, base64);
      return base64;
    } catch (error) {
      console.warn('[photoLoader] Não foi possível converter a foto:', photoUrl, error.message);
      return null;
    } finally {
      clearTimeout(timeoutId);
      inflight.delete(photoUrl);
    }
  })();

  inflight.set(photoUrl, request);
  return request;
};

/**
 * Hook de exibição: entrega o base64 quando disponível e, se a conversão falhar
 * (ex.: fotos antigas do Google Drive, que não liberam CORS), cai para a URL direta
 * apenas para exibição na tela.
 */
export const useStudentPhoto = (photoUrl) => {
  const [state, setState] = useState(() => {
    const cached = getCachedPhoto(photoUrl);
    return cached
      ? { src: cached, isLoading: false, hasError: false, isBase64: true }
      : { src: null, isLoading: !!photoUrl, hasError: false, isBase64: false };
  });

  useEffect(() => {
    if (!photoUrl) {
      setState({ src: null, isLoading: false, hasError: false, isBase64: false });
      return undefined;
    }

    const cached = getCachedPhoto(photoUrl);
    if (cached) {
      setState({ src: cached, isLoading: false, hasError: false, isBase64: true });
      return undefined;
    }

    let active = true;
    setState({ src: null, isLoading: true, hasError: false, isBase64: false });

    fetchPhotoAsBase64(photoUrl).then((base64) => {
      if (!active) return;
      if (base64) {
        setState({ src: base64, isLoading: false, hasError: false, isBase64: true });
      } else {
        // Fallback de exibição: mostra a imagem direto da URL mesmo sem conseguir o base64
        setState({ src: null, isLoading: false, hasError: true, isBase64: false });
      }
    });

    return () => {
      active = false;
    };
  }, [photoUrl]);

  return state;
};
